'use client'

import { Emoji } from '@/components/between-sessions/Emoji'

// The SECOND flagship differentiator. This is what makes Between
// Sessions not-just-a-supervision-service — it's a supervision-plus-
// caseload-engine. Alli receives more client demand than we can serve;
// that demand becomes referrals into BS members. Explicit Psychology
// Today comparison because that's the mental model most therapists
// already have for client matching.
export function LandingReferralsDeepDive() {
  return (
    <section className="px-6 py-24 sm:py-32 bg-[#faf9f6]">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1fr)] gap-16 lg:gap-20 items-center">
        {/* Left: visual — abstract "clients coming to you" flow diagram */}
        <div className="relative order-2 lg:order-1">
          <div className="relative rounded-[2rem] border border-[#ececec] bg-white p-8 sm:p-10 shadow-[0_30px_60px_-30px_rgba(20,20,20,0.2)]">
            <p className="text-[10px] uppercase tracking-[0.22em] text-bs-secondary-700 font-display font-semibold mb-6 text-center">
              How referrals reach you
            </p>

            {/* Step 1: Alli receives client demand */}
            <div className="mb-5">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-bs-primary-500 text-white flex items-center justify-center text-[13px] font-display font-bold">
                  1
                </div>
                <div className="flex-1">
                  <p className="font-display font-semibold text-[15px] text-gray-900">
                    Canadians reach Alli every day looking for therapy
                  </p>
                  <p className="text-[13px] text-gray-600 mt-1 leading-relaxed">
                    More demand than our own team can serve.
                  </p>
                </div>
              </div>
            </div>

            {/* Little pipe */}
            <div className="pl-4 mb-5">
              <div className="w-px h-6 bg-bs-secondary-300 ml-[15px]" />
            </div>

            {/* Step 2: matching */}
            <div className="mb-5">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-bs-primary-500 text-white flex items-center justify-center text-[13px] font-display font-bold">
                  2
                </div>
                <div className="flex-1">
                  <p className="font-display font-semibold text-[15px] text-gray-900">
                    We match on modality, availability, presentation
                  </p>
                  <p className="text-[13px] text-gray-600 mt-1 leading-relaxed">
                    Same idea as Psychology Today — but curated inside the community.
                  </p>
                </div>
              </div>
            </div>

            <div className="pl-4 mb-5">
              <div className="w-px h-6 bg-bs-secondary-300 ml-[15px]" />
            </div>

            {/* Step 3: your caseload grows */}
            <div>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-bs-secondary-500 text-white flex items-center justify-center text-[13px] font-display font-bold">
                  3
                </div>
                <div className="flex-1">
                  <p className="font-display font-semibold text-[15px] text-gray-900">
                    Client lands in your inbox
                  </p>
                  <p className="text-[13px] text-gray-600 mt-1 leading-relaxed">
                    You accept, decline, or refer to another BES therapist — all in one flow.
                  </p>
                </div>
              </div>
            </div>

            {/* Toggle line at bottom */}
            <div className="mt-8 pt-6 border-t border-[#ececec] flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Emoji name="fluent-emoji-high-contrast:pause-button" size={16} />
                <span className="text-[12px] font-display font-medium text-gray-700">
                  Off when you&rsquo;re full
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Emoji name="fluent-emoji-high-contrast:play-button" size={16} />
                <span className="text-[12px] font-display font-semibold text-bs-secondary-700">
                  On when you have capacity
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right: opinion */}
        <div className="order-1 lg:order-2">
          <p className="text-[11px] uppercase tracking-[0.22em] text-bs-secondary-700 font-display font-semibold mb-4">
            Referrals · caseload growth built in
          </p>
          <h2 className="font-display font-bold text-4xl sm:text-5xl leading-[1.05] tracking-tight text-gray-900 mb-6">
            Your caseload,{' '}
            <span className="italic text-bs-secondary-500">growing on its own</span>.
          </h2>
          <p className="text-[17px] text-gray-600 leading-relaxed mb-6">
            Every day more Canadians reach out to{' '}
            <span className="text-gray-900 font-display font-semibold">Alli</span>{' '}
            looking for therapy than our own team can serve. When you&rsquo;re
            ready to take clients, our matching engine sends them straight to
            you — associate-level referrals paired to your modality,
            availability, and specialization.
          </p>
          <p className="text-[17px] text-gray-600 leading-relaxed mb-8">
            It&rsquo;s the same idea as{' '}
            <span className="text-gray-900 font-display font-semibold">
              Psychology Today
            </span>
            &rsquo;s directory — clients discover you, book with you, become
            yours. Except here it&rsquo;s inside a community that also gives
            you supervision, referral tools, and the other therapists you can
            hand cases off to when a fit isn&rsquo;t right.
          </p>

          <ul className="space-y-3">
            {[
              'Turn matching on when you have capacity. Off when you don’t.',
              'Alli’s mission is accessibility — sliding-scale clients ($45+) meet associate ($125–$200) meet full-fee. The demand flows across all price points.',
              'Refer clients between BES therapists in one flow when they’re not the right fit for you.',
              'No extra platform fees. Your rate goes to you.',
            ].map(point => (
              <li key={point} className="flex items-start gap-3">
                <span className="flex-shrink-0 mt-2 w-1.5 h-1.5 rounded-full bg-bs-secondary-500" />
                <span className="text-[15px] text-gray-700 leading-relaxed">
                  {point}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  )
}
